import React from "react";
import {
  COUPON_VALIDATION,
  DISCOUNT_TYPES,
  ZERO,
} from "constants/coupons";
import { isFieldLocked } from "helpers/couponFormDisplay";
import {
  getOptionLabel,
  getTenantOptionDisplayLabels,
  normalizeTenantIdList,
} from "helpers/couponScopeOptions";
import { Icon, QueryErrorPanel } from "widgets";

import CouponFormFieldLabel from "./CouponFormFieldLabel";
import CouponFormToggleSwitch from "./CouponFormToggleSwitch";
import styles from "../CouponForm.module.scss";

const getYesNoLabel = (value) => (value ? "Yes" : "No");

const formatViewValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return value;
};

export const createCouponFormFieldRenderers = ({
  mode,
  values,
  errors,
  updateField,
  isView,
}) => {
  const renderFieldError = (fieldName) =>
    errors[fieldName] ? (
      <p className={styles.fieldError}>{errors[fieldName]}</p>
    ) : null;

  const renderFieldIcon = (fieldName) => {
    const iconName = isFieldLocked(mode, fieldName) ? "lock" : "editOutlined";
    return <Icon name={iconName} className={styles.fieldIconRight} />;
  };

  const renderFieldInput = (
    fieldName,
    children,
    locked = null,
    showIcon = true,
    extraClassName = ""
  ) => {
    const isLocked = locked ?? isFieldLocked(mode, fieldName);
    return (
      <div
        className={`${styles.fieldInput} ${
          isLocked ? styles.fieldInputLocked : ""
        } ${errors[fieldName] ? styles.fieldInputError : ""} ${extraClassName}`}
      >
        {children}
        {showIcon && renderFieldIcon(fieldName)}
      </div>
    );
  };

  const renderReadOnlyField = (fieldName, displayValue) =>
    renderFieldInput(
      fieldName,
      <span className={styles.viewFieldValue}>
        {formatViewValue(displayValue)}
      </span>,
      true,
      true
    );

  const renderYesNoToggle = (fieldName, { disabled = false } = {}) => {
    const value = values[fieldName];
    const isChecked = value === true;
    const isDisabled = disabled || isView;

    return (
      <div className={styles.toggleRow}>
        <CouponFormToggleSwitch
          checked={isChecked}
          onChange={(checked) => updateField(fieldName, checked)}
          disabled={isDisabled}
        />
        <span className={styles.toggleLabel}>{getYesNoLabel(value)}</span>
      </div>
    );
  };

  const renderAmountDecimalHint = () =>
    !isView ? (
      <p className={styles.fieldHint}>
        Not more than {COUPON_VALIDATION.MAX_DISCOUNT_DECIMAL_PLACES} decimal
        places.
      </p>
    ) : null;

  const renderDiscountValueHints = () => {
    if (isView || isFieldLocked(mode, "discountValue")) return null;

    return (
      <>
        {renderAmountDecimalHint()}
        {values.discountType === DISCOUNT_TYPES.PERCENTAGE && (
          <p className={styles.fieldHint}>
            Must be greater than 0 and at most{" "}
            {COUPON_VALIDATION.MAX_PERCENTAGE_DISCOUNT}.
          </p>
        )}
      </>
    );
  };

  const renderSelectField = ({
    fieldName,
    label,
    value,
    options,
    placeholder,
    onChange,
    disabled = false,
    isLoading = false,
    isError = false,
    errorMessage,
    onRetry,
    required = true,
    locked = null,
    emptyHint = null,
  }) => {
    const isLocked = locked ?? isFieldLocked(mode, fieldName);

    return (
      <div className={styles.fieldGroup}>
        <CouponFormFieldLabel required={required}>{label}</CouponFormFieldLabel>
        {isView || isLocked ? (
          renderReadOnlyField(
            fieldName,
            getOptionLabel(options, value) ?? value
          )
        ) : (
          renderFieldInput(
            fieldName,
            <select
              value={value ?? ""}
              onChange={(event) => onChange(event.target.value)}
              disabled={disabled || isLoading}
            >
              <option value="">
                {isLoading ? `Loading ${label.toLowerCase()}...` : placeholder}
              </option>
              {options.map((option) => (
                <option key={option.id} value={String(option.id)}>
                  {option.label}
                </option>
              ))}
            </select>
          )
        )}
        {renderFieldError(fieldName)}
        {emptyHint ? <p className={styles.fieldHint}>{emptyHint}</p> : null}
        {isError ? (
          <QueryErrorPanel
            message={
              errorMessage?.message ?? `Failed to load ${label.toLowerCase()}.`
            }
            onRetry={onRetry}
            className={styles.scopeErrorPanel}
          />
        ) : null}
      </div>
    );
  };

  const renderTenantCheckboxField = ({
    fieldName,
    label,
    selectedIds,
    options,
    placeholder,
    onChange,
    disabled = false,
    isLoading = false,
    isError = false,
    errorMessage,
    onRetry,
    required = true,
    locked = null,
    emptyHint = null,
    showTenantCurrencyBadge = false,
    className = "",
    footer = null,
  }) => {
    const isLocked = locked ?? isFieldLocked(mode, fieldName);
    const normalizedSelectedIds = normalizeTenantIdList(selectedIds);

    const handleTenantCheckboxChange = (optionId, isChecked) => {
      const normalizedOptionId = String(optionId);
      const nextSelectedIds = isChecked
        ? [...normalizedSelectedIds, normalizedOptionId]
        : normalizedSelectedIds.filter(
            (selectedId) => selectedId !== normalizedOptionId
          );

      onChange(nextSelectedIds);
    };

    return (
      <div className={`${styles.fieldGroup} ${className}`.trim()}>
        <CouponFormFieldLabel required={required}>{label}</CouponFormFieldLabel>
        {isView || isLocked ? (
          renderReadOnlyField(
            fieldName,
            getTenantOptionDisplayLabels(options, normalizedSelectedIds, {
              includeCurrencyCode: showTenantCurrencyBadge,
            }) ?? normalizedSelectedIds.join(", ")
          )
        ) : (
          renderFieldInput(
            fieldName,
            <div className={styles.checkboxSelectList}>
              {isLoading || options.length === ZERO ? (
                <span className={styles.checkboxSelectPlaceholder}>
                  {isLoading ? `Loading ${label.toLowerCase()}...` : placeholder}
                </span>
              ) : (
                options.map((option) => {
                  const optionId = String(option.id);
                  const isChecked = normalizedSelectedIds.includes(optionId);
                  const currencyCode = String(option.currencyCode ?? "").trim();

                  return (
                    <label
                      key={option.id}
                      className={styles.checkboxSelectOption}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={disabled || isLoading}
                        onChange={(event) =>
                          handleTenantCheckboxChange(
                            optionId,
                            event.target.checked
                          )
                        }
                      />
                      <span className={styles.checkboxSelectOptionContent}>
                        <span className={styles.checkboxSelectOptionTitle}>
                          {option.label}
                        </span>
                        {showTenantCurrencyBadge && currencyCode !== "" ? (
                          <span className={styles.checkboxSelectOptionBadge}>
                            {currencyCode}
                          </span>
                        ) : null}
                        {option.tenantCode && showTenantCurrencyBadge ? (
                          <span className={styles.checkboxSelectOptionMeta}>
                            {option.tenantCode}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  );
                })
              )}
            </div>,
            false,
            true,
            styles.fieldInputCheckboxSelect
          )
        )}
        {renderFieldError(fieldName)}
        {emptyHint ? <p className={styles.fieldHint}>{emptyHint}</p> : null}
        {isError ? (
          <QueryErrorPanel
            message={
              errorMessage?.message ?? `Failed to load ${label.toLowerCase()}.`
            }
            onRetry={onRetry}
            className={styles.scopeErrorPanel}
          />
        ) : null}
        {footer}
      </div>
    );
  };

  return {
    renderFieldError,
    renderFieldInput,
    renderReadOnlyField,
    renderSelectField,
    renderTenantCheckboxField,
    renderYesNoToggle,
    renderAmountDecimalHint,
    renderDiscountValueHints,
  };
};
